import * as React from 'react';
import { useBoolean } from '@wyz/utils';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Flex,
  FormFeedback,
  IconButton,
  TextField,
  TextFieldProps,
  useConfiguredForm,
} from '../index';
import './text-field.scss';
import { FormTranslateFn } from '@wyz/types';
import { get } from 'lodash-es';

type EditableTextFieldProps = Omit<TextFieldProps, 'type'> & {
  type?: 'text';
};

type PrivateProps<
  TKeys extends string,
  As extends React.ElementType = React.ElementType,
> = EditableTextFieldProps & {
  translate: FormTranslateFn<TKeys>;
  previewAs?: As;
  previewProps?: React.ComponentProps<As>;
};

const EditableTextField = <TKeys extends string>({
  previewAs: Preview = 'span',
  previewProps,
  ...props
}: PrivateProps<TKeys>) => {
  const [isEditable, handlers] = useBoolean(false);
  const configurationContext = useConfiguredForm();
  const methods = useFormContext();
  const errorMessage = get(
    methods.formState.errors,
    `${props.name}.message`,
  ) as unknown as TKeys;

  const value = useWatch({ name: props.name }) as string;
  return (
    <Flex alignItems='center' className='position-relative'>
      {isEditable && configurationContext.mode === 'WRITE' ? (
        <TextField {...props} disabled={false} formGroupClassName='mb-0' />
      ) : (
        <>
          <input
            type='hidden'
            {...methods.register(props.name)}
            value={value}
          />
          <Preview {...previewProps}>{value}</Preview>
          {errorMessage ? (
            <FormFeedback invalid>
              {props.translate(errorMessage, { length: props.maxLength })}
            </FormFeedback>
          ) : null}
        </>
      )}
      {configurationContext.mode === 'WRITE' && (
        <div>
          {!isEditable && (
            <IconButton
              variant='link'
              size='sm'
              id={`toggle-edit-mode-${props.name}`}
              icon='pencil-line'
              onClick={(e) => {
                e.stopPropagation();
                handlers.setTrue();
                // Note setTimeout is mandatory, so that RHF will have the time to focus the input after being enabled in the last statement
                setTimeout(() => methods.setFocus(props.name));
              }}
            />
          )}
          {isEditable && (
            <IconButton
              variant='link'
              size='sm'
              id={`submit-edit-mode-${props.name}`}
              icon='check-box-circle'
              onClick={(e) => {
                e.stopPropagation();
                methods
                  .trigger(props.name)
                  .then((res) => {
                    if (res === true) {
                      handlers.setFalse();
                    }
                  })
                  .catch(() => {});
              }}
            />
          )}
        </div>
      )}
    </Flex>
  );
};

export default EditableTextField;
